<template>
  <div class="full-height" data-test-id="program-admin-v3">
    <div
      width="100%"
      rounded="0"
      class="full-height"
      id="program-manager"
      :class="!isMobile ? 'd-flex flex-column' : ''"
    >
      <!-- Header card/row -->
      <AdminHeader
        title="Program Management"
        icon="mdi-star"
        iconColor="brand"
        class="mb-5"
        :class="!isMobile ? 'mx-6' : 'mx-3'"
        :elevation="1"
        :showSearch="true"
        @update-search="handleSearchValueChange"
      />

      <!-- Visible Messages row -->
      <RecommendationCards
        :messages="messages"
        :isMobile="isMobile"
        :max-cards="maxRecommendationCards"
        :class="!isMobile ? 'mx-3' : 'mx-0'"
        @card-action="handleRecommendationCardAction"
      />

      <!-- Options row -->
      <v-row
        class="d-flex justify-space-between flex-grow-1 mt-0"
        :style="isMobile ? {} : { 'max-height': '60%' }"
        :class="{
          'flex-column': isMobile,
          'px-6': !isMobile,
        }"
      >
        <!-- Navigation Sidebar -->
        <v-col :cols="isMobile ? '12' : '4'" class="card-col">
          <AdminNavigationSidebar
            :current-view="viewFilter"
            :is-mobile="isMobile"
            :navigation-items="navigationItems"
            :other-actions="otherActions"
            @navigation-change="handleViewChange"
            @action-click="handleOtherActionClick"
          />
        </v-col>
        <AdminContentArea
          :current-view="viewFilter"
          :search="table.search"
          :is-mobile="isMobile"
          :source-page="sourcePage"
          :programTableOptions="table"
          :display-loading-icon-on-program="displayLoadingIconOnProgram"
          :deleting-program="deletingProgram"
          :expanded-programs="programsExpanded"
          :budgets="budgets"
          @message-program-audience="messageProgramAudience"
          @rewards-action="loadPaymentAction"
          @load-program-wizard="loadProgramWizard"
          @load-metric-ui="loadMetricUI"
          @get-budget="getBudget"
          @expand-program="expandProgram"
          @collapse-program="collapseProgram"
          @program-delete-confirmation="deleteProgramConfirmation"
        />
      </v-row>
    </div>
    <!-- Nav drawers -->
    <v-navigation-drawer v-model="metricCreatorWidget" temporary fixed right width="500">
      <MetricCreatorWidget @close="metricCreatorWidget = false" :clientId="clientId" />
    </v-navigation-drawer>
    <!-- Dialogs -->
    <!-- Metric Management Dialog -->
    <v-dialog
      fullscreen
      v-model="metricManagementWidget"
      transition="dialog-bottom-transition"
      class="z-index-front"
    >
      <v-card>
        <MetricDefinition
          v-if="metricManagementWidget"
          :metrics="metrics"
          :loading="loadingMetrics"
          @get-metrics="getMetrics"
          @close="metricManagementWidget = false"
          @update-client="refreshClientId"
        ></MetricDefinition>
      </v-card>
    </v-dialog>
    <!-- Deletion confirmation dialog -->
    <DeleteConfirmationDialog
      :value="dialogProgramDelete"
      @input="dialogProgramDelete = $event"
      :title="
        programDeletionError
          ? 'We ran into a problem while deleting the program'
          : 'Are you sure you want to delete this program?'
      "
      :confirmation-message="
        programDeletionError
          ? 'Please try again or contact us if the problem continues.'
          : 'This program will be permanently deleted for all users within the program. This action cannot be undone.'
      "
      item-type="program"
      :item-name="programNameToDelete"
      :require-delete-confirmation="true"
      :loading="deletingProgram"
      @confirm="deleteProgram(programIdToDelete)"
      @cancel="dialogProgramDelete = false"
    />
    <!-- upload metrics dialog -->
    <v-dialog v-model="dialogUploadMetrics" width="450">
      <v-card>
        <MetricUpload
          :metrics="metrics"
          v-if="dialogUploadMetrics"
          @close="dialogUploadMetrics = false"
          :disalbled="loadingMetrics || metrics.length == 0"
          @update-client="refreshClientId"
        >
        </MetricUpload>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// @ is an alias to /src
import AdminHeader from "@/components/Header/Header.vue";
import AdminNavigationSidebar from "@/components/admin/AdminNavigationSidebar.vue";
import RecommendationCards from "@/components/admin/RecommendationCards.vue";
import DeleteConfirmationDialog from "@/components/admin/DeleteConfirmationDialog.vue";

import AdminContentArea from "@/components/program/admin/ProgramAdminV3/AdminContentArea/AdminContentArea.vue";
import MetricCreatorWidget from "@/components/program/admin/MetricCreatorWidget.vue";
import MetricDefinition from "@/components/program/admin/MetricDefinition.vue";
import MetricUpload from "@/components/program/admin/MetricUpload.vue";

import ProgramService from "@/services/ProgramService";
import BudgetService from "@/services/BudgetService";

import moment from "moment";
import { mapState } from "vuex";

import clientFieldsMixin from "@/mixins/clientFieldsMixin";
import clientFeatureFlagsMixin from "@/mixins/clientFeatureFlags";
import { debounce } from "@/shared_data/functions";

const keys = {
  programs: "programs",
  program: "programs",
  forms: "forms",
  rewards: "rewards",
  learning: "learning",
  surveys: "surveys",
  library: "library",
  "claims-config": "claims-config",
};
export default {
  name: "ProgramAdminV3",
  title: "Program Management",
  components: {
    // Generic
    AdminHeader,
    AdminNavigationSidebar,
    RecommendationCards,
    DeleteConfirmationDialog,

    AdminContentArea,
    MetricCreatorWidget,
    MetricDefinition,
    MetricUpload,
  },
  mixins: [
    clientFieldsMixin(
      {
        clientHasInformTenant: "informTenantId",
        dynamicDataProgramsEnabled: "dynamicDataProgramsEnabled",
      },
      { fresh: true }
    ),
    clientFeatureFlagsMixin,
  ],
  props: {
    preload: {
      type: String,
      defualt: null,
    },
    // Used if we came from somewhere else and immediately loaded the payments console
    previousRoute: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      keys,
      // STUFF TO KEEP
      // Colors - Chris is using not brand colors in the wireframes
      // Using his colors but making it easy to swap
      offBrandCyan: "#4DC9F9",
      offBrandBrightGreen: "#A2D76D",
      sourcePage: "programadmin",
      loadingPrograms: false,
      programs: null,
      dialog: { learningPage: true },
      viewFilter: keys.programs, // 1: All programs for client, 0: Universal
      navigationItems: [
        {
          key: keys.programs,
          label: "Programs",
          permission: "programs:create:program",
        },
        {
          key: keys.forms,
          label: "Forms",
          permission: "vue:read:formadmin",
        },
        {
          key: keys.rewards,
          label: "Rewards library",
          permission: "wallets:create:cashpayment",
        },
        {
          key: keys.learning,
          label: "Learning management",
          permission: "learning:create:module",
        },
        {
          key: keys.surveys,
          label: "Survey management",
          permission: "learning:create:module",
        },
        {
          key: keys.library,
          label: "Program library",
          permission: "programs:create:program",
        },
        {
          key: keys["claims-config"],
          label: "Claims configuration",
          permission: "users:update:client", // Client configuration setting, so they need admin privileges
        },
      ],
      otherActions: [
        {
          key: "updateProgress",
          label: "Update Progress",
          permission: "programs:create:program",
        },
        {
          key: "manageMetrics",
          label: "Manage Metrics",
          permission: "programs:create:program",
        },
        {
          key: "uploadMetrics",
          label: "Upload Metrics",
          permission: "programs:create:program",
        },
        {
          key: "programReporting",
          label: "Program Reporting",
          permission: "vue:read:reporting",
        },
      ],
      metricCreatorWidget: false,
      metricManagementWidget: false,
      dialogProgramDelete: false,
      programIdToDelete: null,
      programNameToDelete: null,
      programDeletionConfirmation: null,
      programDeletionError: false,
      displayLoadingIconOnProgram: null, // an id to dipslay a loading icon on
      deletingProgram: false,
      search: null,
      budgets: {},
      metrics: [],
      loadingMetrics: false,
      dialogUploadMetrics: false,
      programsExpanded: [],
      table: {
        itemsPerPage: 10,
        page: 1,
        // Used if the user ends up mashing the table page button, then we're waiting on multiple responses so we know which page to actually store the data for
        awaitingPage: 1,
        search: null,
        debounceSearch: null,
        loading: false,
        programs: [],
        total: 0,
        selected: [],
        options: {},
        footerProps: {
          "items-per-page-options": [10, 25, 50, 100],
        },
        latestFilter: null,
        latestSort: null,
      },
    };
  },
  created() {
    if (this.$route.name == "learningadmin" || this.$route.name == "surveyadmin") {
      this.showLearningAdminPopup(this.$route.name);
    } else {
      const pathFromQuery = this.resolveViewFromQuery();
      if (pathFromQuery != null) {
        this.viewFilter = pathFromQuery;
      } else {
        const firstPermitted = this.navigationItems.find((item) =>
          this.permissions.includes(item.permission)
        );
        if (firstPermitted) this.viewFilter = firstPermitted.key;
      }
    }

    if (this.permissions.includes("programs:create:program")) {
      this.getMetrics();
    }
  },

  mounted() {
    // Preloads the builder's claims flag so opening the builder doesn't wait on it.
    this.loadClientFeatureFlags().catch((e) =>
      console.error("--- CLIENT FEATURE FLAGS PRELOAD FAILED ---", e)
    );
  },
  unmounted() {},
  methods: {
    expandProgram(value) {
      console.log("Expanding program ", value);
      this.programsExpanded.push(value);
    },
    collapseProgram(value) {
      this.programsExpanded = this.programsExpanded.filter((x) => x != value);
    },
    async getProgramsV2(reset = false, source = null) {
      try {
        console.log("Getting programs from ", source);
        this.table.loading = true;
        if (reset) this.table.options.page = 1;
        let options = this.table.options;
        let page = this.table.options.page || 1;
        let search = this.table.search ? this.table.search.replace(/'/g, "\\'") : null;
        let filter = `status != 'Deleted' && (type == null || type != 'procore') && clientId = ${
          this.viewFilter == this.keys.library ? 0 : this.clientId
        }`;

        if (search) {
          // If search we need to search for every field that's in the table
          filter += ` && (displayName.like('%${search}%') || status.like('%${search}%') || programId.like('%${search}%'))`;
        }
        let limit = options.itemsPerPage;
        let offset = reset ? 0 : (page - 1) * limit;
        if (this.table.options.sortBy && options.sortBy.length > 0) {
          let column = options.sortBy;
          let type =
            options.sortDesc && options.sortDesc.length > 0 && options.sortDesc[0] === true
              ? "DESC"
              : "ASC";

          if (column == "formattedSendDate") sort = `sendDate ${type}`;
          else var sort = column + " " + type;
        } else sort = "programId DESC";
        this.table.latestSort = sort;
        this.table.latestFilter = filter;
        let params = {
          filter,
          offset,
          limit,
          sort,
          // Program Cache is used to get group estimates
          // Program Element is used so we know when to show update progress
          expand: "ProgramCache,ProgramElement",
        };
        let response = await ProgramService.getProgramsV2(params);

        // we have to compare states to see if we ignore results (only if them spam a filter)
        if (filter != this.table.latestFilter || sort != this.table.latestSort) {
          console.log("Ignoring response");
          return;
        }

        console.log("Got programs ", response);
        this.table.total = response.result.count;
        // if (reset || wipeArray)
        //   this.table.messages = new Array(this.table.total).fill(undefined);
        let rows = response.result.rows.map((r) => {
          r.formattedSendDate = new moment(r.sendDate).local().format("YYYY-MM-DD h:mm a");

          var formattedStartDate = null;
          if (r.startDate)
            formattedStartDate = moment(r.startDate, "YYYY-MM-DD hh:mm:ss").format("MMM Do, YYYY");

          var formattedEndDate = null;
          if (r.endDate)
            formattedEndDate = moment(r.endDate, "YYYY-MM-DD hh:mm:ss").format("MMM Do, YYYY");

          r.formattedStartDate = formattedStartDate;
          r.formattedEndDate = formattedEndDate;
          r.formattedRollingProgram = r.rollingProgramLength
            ? `${r.rollingProgramLength} ${r.rollingProgramLength == 1 ? "day" : "days"}`
            : null;
          r.nonLearningMetrics = !!r.ProgramElements.find((x) => x.metricId);

          r.totalParticipants = r.ProgramCaches.filter((x) => x != "Deleted");
          r.notStartedParticipants = r.ProgramCaches.filter(
            (x) =>
              (!x.progressPercent && !x.progressValue) || // No progress percent or progress value
              x.progressPercent == "0" || // Progress percent is 0
              (!x.progressPercent && x.progressValue && x.progressValue == "0") // No progress percent but progress value is 0
          );
          r.inProgressParticipants = r.ProgramCaches.filter(
            (x) =>
              (Number(x.progressPercent) > 0 && Number(x.progressPercent < 100)) || // progress percent between 1 & 100
              (!x.progressPercent && Number(x.progressValue) > 0 && Number(x.progressValue) < 100) // no progress percent but progress value between 1 & 100
          );
          r.completedParticipants = r.ProgramCaches.filter(
            (x) =>
              Number(x.progressPercent) >= 100 || // progress percent greater than or equal to 100
              (!x.progressPercent && Number(x.progressValue) >= 100) // no progress percent but progress value greater than or equal to 100
          );

          return r;
        });

        this.table.programs = rows;
        this.table.loading = false;

        // We can reset what is expanded
        this.programsExpanded = [];
      } catch (err) {
        console.log("Error getting programs ", err);
        this.table.loading = false;
      }
    },
    async getMetrics() {
      this.loadingMetrics = true;
      await ProgramService.getMetrics()
        .then((response) => {
          var metrics = [];
          response.forEach((metric) => {
            if (metric.status !== "Deleted") {
              metrics.push(metric);
            }
          });
          this.metrics = metrics;
        })
        .catch((error) => {
          console.log("Error getting metrics", error);
        })
        .finally(() => {
          this.loadingMetrics = false;
        });
    },
    // fetches budget details, what we really want is the historicalBalance to display the og amount loaded
    async getBudget(p) {
      // If the program has a budget and we haven't fetched the current info, fetch
      if (!p.budgetId || this.budgets[p.budgetId])
        return console.log("No need to fetch program budget ", p.budgetId);
      // array of loading budget indexes
      let program = this.table.programs.find((x) => x.programId == p.programId);
      if (!program) return console.log("Couldn't find program in array");
      program.Budget = { loading: true };

      const budgetId = p.budgetId;
      let obj = {
        loading: true,
      };
      this.budgets[budgetId] = obj;
      try {
        let res = await BudgetService.getBudget(budgetId);
        console.log(`GOT BUDGET ${budgetId} `, res);

        // Attach historical account balance to program
        if (!res.Award || res.budgetBalance === undefined || res.budgetBalance === null)
          throw "Missing award or budget balance";

        // If award id = 1, prepend = "$"
        const prepend = res.Award.awardId == 1 ? "$" : "";
        // Else if Award.awardId != 1 && award.awardDisplayName, append " awardDisplayName"
        const append =
          res.Award.awardId != 1 && res.Award.awardDisplayName
            ? " " + res.Award.awardDisplayName
            : "";

        obj.formattedOriginalFundedAmount = `${prepend}${res.historicalAccountBalance}${append}`;
        obj.originalFundedAmount = res.historicalAccountBalance;
        obj.formattedCurrentBudgetBalance = `${prepend}${res.budgetBalance}${append}`;
        obj.currentBudgetBalance = res.budgetBalance;
        obj.loading = false;
      } catch (err) {
        console.log("Error getting budget for program id", p.programId);
        console.log(err);
        // This will stop the loading and display error message
        obj.errorFetching = true;
        obj.loading = false;
      }

      this.budgets[budgetId] = obj;

      // Set the program to itself so it re-renders
      this.$set(program, {
        ...program,
      });
    },
    deleteProgram(programId) {
      this.displayLoadingIconOnProgram = programId;
      this.deletingProgram = true;
      ProgramService.deleteProgram(programId)
        .then((response) => {
          console.log("Delete program response: ", response);
          this.getProgramsV2();
          this.programIdToDelete = null;
          this.programNameToDelete = null;
          this.programDeletionConfirmation = null;
          this.dialogProgramDelete = false;
        })
        .catch((error) => {
          console.log("Error deleting program ", error);
          this.programDeletionError = true;
        })
        .finally(() => {
          this.displayLoadingIconOnProgram = null;
          this.deletingProgram = false;
        });
    },
    handleRecommendationCardAction(message) {
      switch (message.action) {
        case "program-wizard":
          this.loadProgramWizard();
          break;
        case "onboarding-program":
          this.loadProgramWizard();
          break;
        case "survey-program":
          this.loadSurveyBuilder();
          break;
        case "learning-module":
          this.$router.push({
            name: "learningwizard",
            params: {
              clientId: this.clientId,
              preloadedClientId: this.clientId,
              editingLearning: false,
              sourcePage: this.sourcePage,
              sourcePageProps: {
                preload: "learning",
              },
            },
            query: {
              clientId: this.clientId,
            },
          });
          break;
        case "update-progress":
          this.loadMetricUI(null);
          break;
        case "dynamic-data-program":
          this.loadDynamicDataProgramBuilder();
          break;
      }
    },
    loadDynamicDataProgramBuilder() {
      this.$router.push({ name: "dynamicdataprogrambuilder" });
    },
    handleOtherActionClick(key) {
      switch (key) {
        case "updateProgress":
          this.loadMetricUI(null);
          break;
        case "manageMetrics":
          this.loadMetricManagement();
          break;
        case "uploadMetrics":
          this.loadMetricCSV();
          break;
        case "programReporting":
          this.loadReporting();
          break;
      }
    },
    loadProgramWizard(program = null, editing, step) {
      // Editing a dynamic-data program routes to the new builder; every other
      // case (including duplicate of any type) still uses ProgramWizard.
      if (program && editing && program.type === "dynamic-data") {
        this.$router.push({
          name: "dynamicdataprogrambuilder",
          query: {
            programId: program.programId,
            clientId: program.clientId,
          },
        });
        return;
      }
      if (program) {
        // Called from inside table, we pass in program and if we're editing
        this.$router.push({
          name: "programwizard",
          query: {
            programId: program.programId,
            clientId: program.clientId,
            duplicate: !editing,
          },
          params: {
            preloadedStep: editing && step ? step : null,
          },
        });
      } else {
        this.$router.push({ name: "programwizard" });
      }
    },
    loadSurveyBuilder() {
      this.$router.push({
        name: "surveybuilder",
        params: {
          previousRoute: this.$route.name,
          preloadedClientId: this.clientId,
          previousRouteProps: {
            preload: "surveys",
          },
        },
      });
    },
    loadMetricUI(programId = null) {
      let params = {};
      if (programId) params.programId = programId;
      this.$router.push({ name: "progress", params });
    },
    loadMetricCreator() {
      this.metricCreatorWidget = true;
    },
    loadMetricManagement() {
      this.metricManagementWidget = true;
    },
    loadMetricCSV() {
      this.dialogUploadMetrics = true;
    },
    handleViewChange(key) {
      this.viewFilter = key;
      if (this.table.options) this.table.options.page = 1;
      this.$router
        .replace({
          name: this.$route.name,
          query: { ...this.$route.query, tab: key },
        })
        .catch(() => {});
    },

    /**
     * Resolves viewFilter from route.query.tab or preload prop.
     * Uses the same permission check: must be a valid nav key and user must have permission.
     * @returns {string|null} The key to use for viewFilter, or null if none valid/permitted.
     */
    resolveViewFromQuery() {
      const candidates = [this.$route.query.tab, this.preload].filter(
        (v) => v != null && typeof v === "string" && v !== ""
      );
      for (const key of candidates) {
        const item = this.navigationItems.find((nav) => nav.key === key);
        if (item && this.permissions.includes(item.permission)) return key;
      }
      return null;
    },
    loadReporting() {
      this.$router.push({ name: "reporting" });
    },
    refreshClientId() {
      console.log("Refreshing client");
      this.getProgramsV2();
    },
    // For v-progress-linear needs a buffer value of 100 to take up full width
    // Ex: (inProgressParticipants / totalParticipants) * 100
    calculatePercentage(num1, num2) {
      return (num1 / num2) * 100;
    },
    messageProgramAudience(program, filter) {
      this.$router.push({
        name: "communicationwizard",
        query: {
          previousRoute: this.$route.name,
          programId: program && program.programId ? program.programId : program,
          programFilter: filter,
          context: "program",
        },
        params: {
          programId: program && program.programId ? program.programId : program,
          programFilter: filter,
        },
      });
    },

    deleteProgramConfirmation(program) {
      this.dialogProgramDelete = true;
      this.programIdToDelete = program.programId;
      this.programNameToDelete = program.displayName;
      this.programDeletionConfirmation = null;
      this.programDeletionError = false;
    },
    loadPaymentAction(message) {
      if (!message.action) return;
      switch (message.action) {
        case "one-time-payment":
          this.$router.push({
            name: "paymentwizard",
            params: {
              previousRoute: "programadmin",
            },
          });
          break;
        case "budget-wizard":
          console.log("Loading budget wizard", message.label);
          this.$router.push({
            name: "budgetwizard",
            params: {
              previousRoute: "programadmin",
              previousRouteProps: {
                preload: "rewards",
              },
              paymentTileName: message.label,
            },
          });
          break;
        case "recurring-payment":
          this.$router.push({
            name: "paymentadmin",
            params: {
              preload: "payment",
              previousRoute: "programadmin",
              previousRouteProps: {
                preload: "rewards",
              },
            },
          });
          break;
        case "annual-reward-birthday":
          console.log("loading birthday reward");
          this.loadPaymentConfigWizard("BIRTHDAY");
          break;
        case "annual-reward-service-anniversary":
          console.log("loading service anniversary");
          this.loadPaymentConfigWizard("SERVICE_ANNIVERSARY");
          break;
      }
    },
    loadPaymentConfigWizard(type = undefined) {
      this.$router.push({
        name: "annualrewardwizard",
        params: {
          previousRoute: "programadmin",
          previousRouteProps: { preload: "rewards" },
          type,
        },
      });
    },
    showLearningAdminPopup(route) {
      if (route == "learningadmin") {
        this.viewFilter = this.keys.learning;
        this.dialog.learningPage = true;
        this.$router.replace({ name: "programadmin" });
      } else if (route == "surveyadmin") {
        this.viewFilter = this.keys.surveys;
        this.dialog.learningPage = false;
        this.$router.replace({ name: "programadmin" });
      }
    },
    handleSearchValueChange(val) {
      this.table.search = val;
      this.getProgramsV2(true, "search");
    },
  },
  computed: {
    ...mapState(["userProfile", "permissions", "clients", "globalClientId"]),
    clientId: {
      get: function () {
        return this.globalClientId;
      },
      set: function (newVal) {
        this.$store.dispatch("setClientId", newVal);
      },
    },
    messages() {
      const cards = [
        {
          title: "Create a new program",
          content: "Use the program builder to create a new program from the ground up.",
          icon: "mdi-star",
          iconColor: "systemPrimary",
          action: "program-wizard",
          permission: "programs:create:program",
        },
        {
          title: "Create a learning module",
          content:
            "Use the learning builder to create a new module to use in a program. You can even reward those who take the learning module!",
          icon: "mdi-school",
          iconColor: "systemPrimary",
          action: "learning-module",
          permission: "vue:read:learningadmin",
        },
        {
          title: "Create a survey",
          content:
            "Use the survey builder to create a survey that can be used in a program. Surveys can also stand alone and are either public or private.",
          icon: "mdi-comment-question",
          iconColor: "systemPrimary",
          action: "survey-program",
          permission: "vue:read:surveyadmin",
        },
      ];

      if (this.showDynamicProgramCard) {
        cards.push({
          title: "Create a new dynamic data program",
          content: "Create a sales incentive or claim program using the dynamic rule builder.",
          icon: "mdi-star",
          iconColor: "systemPrimary",
          action: "dynamic-data-program",
          permission: "programs:create:program",
        });
      }

      cards.push({
        title: "Update your program progress",
        content: "Keep your programs up to date with the latest data.",
        icon: "mdi-checkbox-marked-outline",
        iconColor: "systemPrimary",
        action: "update-progress",
        permission: "programs:create:program",
      });

      return cards;
    },
    // The builder's rule sets live in the client's Inform tenant, so it is unusable without one.
    showDynamicProgramCard() {
      return this.dynamicDataProgramsEnabled && this.clientHasInformTenant;
    },
    // Bump the visible-card cap to 4 when the dynamic-program card is shown so it is
    // reachable; otherwise preserve the historical 3-card cap.
    maxRecommendationCards() {
      return this.showDynamicProgramCard ? 4 : 3;
    },
    visiblePrograms() {
      let programs = this.table.programs || this.programs || [];
      if (this.viewFilter !== null) {
        // Program library
        if (this.viewFilter == this.keys.library) {
          programs = programs.filter((x) => x.status !== "Deleted" && x.clientId == 0);
        } else {
          // if (this.clientId === 1) {
          //   programs = programs.filter(x => x.status != "Deleted");
          // } else {
          programs = programs.filter((x) => x.status != "Deleted" && x.clientId == this.clientId);
          // }
        }
      }

      if (this.table.loading) return [];
      return programs;
      // return !this.search
      //   ? programs
      //   : programs.filter(x =>
      //       x.displayName.toLowerCase().includes(this.search.toLowerCase())
      //     );
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    computedHeaders() {
      if (this.viewFilter == this.keys.programs) {
        return [
          {
            text: "Id",
            value: "programId",
            width: "10%",
            align: this.userProfile && this.userProfile.clientId == 1 ? "start" : " d-none",
          },
          {
            text: "Program Name",
            value: "displayName",
            width: "50%",
          },
          {
            text: "Status",
            value: "status",
            width: "40%",
          },
          { text: "Details", value: "data-table-expand", width: "10%" },
        ];
      } else {
        return [
          {
            text: "Program Name",
            value: "displayName",
            width: "50%",
          },
          {
            text: "",
            value: "actionItems",
            width: "50%",
          },
        ];
      }
    },
  },
  watch: {
    // Same value as globalClientId, whose watcher (clientFieldsMixin) refreshes the client fields.
    clientId: function (newVal, oldVal) {
      // If client id changes, we get budgets!
      if (
        newVal != oldVal &&
        oldVal != null &&
        (this.viewFilter === this.keys.library || this.viewFilter === this.keys.programs)
      ) {
        this.getProgramsV2();
      }
    },
    "table.options": {
      handler(newVal, oldVal) {
        console.log("New options ", newVal);
        console.log("Old options ", oldVal);
        // Used to indicate if we wipe the table and start with an empty array, versus pushing more data on
        var reset = false;
        var apiCall = true;
        // If none of these exist then we're probably loading the page for the first time so we reset
        if (!oldVal.itemsPerPage || !oldVal.page || !oldVal.sortBy || !oldVal.sortDesc)
          reset = true;
        else {
          // If any values changed from their old to new states, then we reset because they're changing a sort
          if (
            newVal.itemsPerPage !== oldVal.itemsPerPage ||
            (newVal.sortBy && oldVal.sortBy && newVal.sortBy[0] !== oldVal.sortBy[0]) ||
            (newVal.sortDesc && oldVal.sortDesc && newVal.sortDesc[0] !== oldVal.sortDesc[0])
          )
            reset = true;
        }

        if (apiCall) this.getProgramsV2(reset, "watcher");
      },
      deep: true,
    },
    "table.debounceSearch": debounce(function (newVal) {
      this.table.search = newVal;
      if (this.viewFilter === this.keys.library || this.viewFilter === this.keys.programs)
        this.getProgramsV2(true, "debounce search");
    }, 500),
    "$route.query.tab"() {
      if (this.$route.name === "learningadmin" || this.$route.name === "surveyadmin") return;
      const resolved = this.resolveViewFromQuery();
      if (resolved != null) this.viewFilter = resolved;
    },
  },
};
</script>

<style scoped>
.border-radius {
  border-radius: 5px;
}

/* Hides gray background when clicking v-card */
.v-card--link:before {
  background: none;
}

.icon {
  min-width: 52px;
  max-width: 52px;
  width: 52px;
  min-height: 52px;
  max-height: 52px;
  height: 52px;
}

@media (max-width: 960px) {
  .icon {
    min-width: 40px;
    max-width: 40px;
    width: 40px;
    min-height: 40px;
    max-height: 40px;
    height: 40px;
  }
}
/* Changes the default padding of all cols that have a card in them in the payment manager div */
#program-manager .card-col.col {
  padding-top: 8px;
  padding-bottom: 8px;
}

#program-manager .v-card {
  border-radius: 10px;
  padding-right: 12px;
  padding-left: 12px;
}
</style>
